import React, { useCallback, useEffect, useMemo, useRef, useState } from "react"
import {
    Alert, Button, Card, Col, Modal, Radio, Row, Select, Space, Switch, Table, Tabs, Tooltip,
} from "antd"
import dayjs from "dayjs"
import { jsPDF } from "jspdf"
import { DownOutlined, ExclamationCircleFilled, LinkOutlined, UpOutlined } from "@ant-design/icons"
import CreditSummaryTableV2 from "../CrediReport/CreditSummaryTableV2"
import CreditSummaryReviewV2 from "../CrediReport/CreditSummaryReviewV2"
import { checkAccess } from "../Auth"
import { UR_Owner } from "../State"
import { useHistory } from "react-router-dom"
import { getLosApplicationV2, postLosParticipantCreditPullV2, postLosCreditPullPaymentRequestV2, getLosCreditPullPaymentRequestV2, deleteLosCreditPullPaymentRequestV2 } from "../DocumentChecklist/applicationV2Client"
import {
    buildCreditOwnershipOptions,
    formatCreditOwnershipLabel,
    isCreditOwnershipEditable,
    resolveOwnershipReportContext,
} from "./creditImportV2"
import {
    REPORT_TYPE_BY_RADIO,
    VENDORS_LIST,
    buildCreditReportsTabParties,
    buildReportSelectOptions,
    fetchCreditAccounts,
    fetchCreditReports,
    formatParticipantAddress,
    formatParticipantName,
    formatCreditPullSubjectsLabel,
    listCreditPullProfileGaps,
    withCreditPullSubjectError,
    listParticipants,
    loadConsentState,
    maskSSN,
    reportSummaryTitle,
    buildAllCreditPullOperations,
    buildBorrowerPaidPaymentOps,
    estimateBorrowerPaidAmount,
    isBorrowerPaidHardPull,
    resolveSpousePairJointCardFlags,
    resolvePullSubjects,
    canChooseCreditPullMode,
    saveConsentState,
    loadCreditPullPrefs,
    saveCreditPullPrefs,
    applyCreditPullPrefs,
    isReportComplete,
    isReportPending,
    isReportUsable,
    reportStatusLabel,
    reportVendorMessage,
    resumeCreditReport,
} from "./creditReportV2Utils"
import { formatMoney as formatPriceMoney } from "../Management/CreditReportSetup/creditPricingUtils"
import { track } from 'statsig'
import { STATSIG } from 'statsig/events'
import "../CreditReport.css"
import "../newCreditReport.css"

const { TabPane } = Tabs
const { Option } = Select

// Auto-collect cadence for a pull the bureau had not finished when it returned. Bounded on
// purpose: every tick is a real status query against the bureau, so a page left open
// overnight must not keep asking forever. Ticks are only spent while the tab is visible,
// so this is 20 minutes of someone actually watching, not 20 minutes of wall clock.
const AUTO_COLLECT_INTERVAL_MS = 30000
const AUTO_COLLECT_MAX_TICKS = 40

const partiesReplace = [
    { role: "", value: "", title: "ExperianFairIsaac", factors: [] },
    { role: "", value: "", title: "FICORiskScoreClassic04", factors: [] },
    { role: "", value: "", title: "EquifaxBeacon5.0", factors: [] },
]
const REPOSITORY_BY_MODEL = {
    ExperianFairIsaac: "Experian",
    "EquifaxBeacon5.0": "Equifax",
    TransUnion: "TransUnion",
    FICORiskScoreClassic: "TransUnion",
}

const formatMoney = (value) => {
    const n = Number(value || 0)
    return Number.isNaN(n) ? "$0.00" : `$${n.toFixed(2)}`
}

const paymentRequestField = (req, camel, pascal) => {
    if (!req) return undefined
    return req[camel] !== undefined ? req[camel] : req[pascal]
}

const formatPendingPaymentSubtitle = (req) => {
    if (!req) return ""
    const amount = formatPriceMoney(paymentRequestField(req, "amount", "Amount"))
    const items = paymentRequestField(req, "lineItems", "LineItems") || []
    let kind = "report"
    if (items.length === 1) {
        const mode = (items[0].pullMode || items[0].PullMode || "").toLowerCase()
        kind = mode === "joint" ? "joint report" : "individual report"
    }
    return `Borrower paid · ${amount || "$0.00"} (${kind})`
}

/** LO-facing short reason from CRA / pay lastError. */
const friendlyPaymentLastError = (raw) => {
    const text = `${raw || ""}`.trim()
    if (!text) return "Payment was attempted but did not complete"
    if (/\b(declin|insufficient funds)\b/i.test(text)) return "Card declined"
    if (/expir/i.test(text)) return "Card expired"
    if (/\b(avs|zip|postal)\b/i.test(text)) return "Card could not be verified"
    if (/xml|mismo|ServicePayment|xmlns|schemas:/i.test(text) || text.length > 140) {
        return "Payment could not be completed"
    }
    return text
}

const camelCaseToSpaces = (str = "") => `${str}`.replace(/([a-z])([A-Z])/g, "$1 $2")

const bureauNameFromModel = (title = "") => {
    if (title.includes("Equifax")) return "Equifax"
    if (title.includes("TransUnion") || title.includes("FICORiskScoreClassic")) return "TransUnion"
    if (title.includes("Experian")) return "Experian"
    return title
}

const bureauLogoFromModel = (title = "") => {
    if (title.includes("Equifax")) {
        return <img style={{ marginTop: 6 }} src="/images/equifax.svg" alt="Equifax" />
    }
    if (title.includes("TransUnion") || title.includes("FICORiskScoreClassic")) {
        return <img src="/images/transunion.svg" alt="TransUnion" />
    }
    if (title.includes("Experian")) {
        return <img src="/images/experian.svg" alt="Experian" />
    }
    return null
}

const formatCreditConsentDate = (dateString) => {
    if (!dateString) return "--"
    const d = dayjs(dateString)
    if (!d.isValid()) return "--"
    return `${d.toDate().toLocaleString("en-US", {
        timeZone: "America/Los_Angeles",
        dateStyle: "long",
        timeStyle: "short",
    })} PST`
}

const formatParticipantDob = (dateString) => {
    if (!dateString) return "--"
    const d = dayjs(dateString)
    return d.isValid() ? d.format("MM/DD/YYYY") : "--"
}

const buildCreditAuthorizationPdf = ({ acceptances, companyName }) => {
    const rows = (acceptances || []).filter((row) => row && row.name)
    if (rows.length === 0) return
    const line2 = `Accepted for ${companyName || "—"}`
    const line3 = 'By clicking the "I/We consent to a credit check..." checkbox, I/we hereby grant permission to obtain all information deemed necessary to process this mortgage loan application. This information includes, but is not limited to, past and present employment status, deposit accounts, past and present consumer credit record, mortgage record, and/or rental record.'
    const doc = new jsPDF("p", "pt", "a4")
    const margin = 72
    const maxWidth = doc.internal.pageSize.getWidth() - margin * 2
    const lineHeight = 14
    let y = margin
    doc.setFontSize(11)
    doc.setTextColor(255, 0, 0)
    rows.forEach((row) => {
        const line1 = `Accepted by ${row.name} (${row.email || "—"}) on ${row.consentDate || "—"}`
        const wrapped = doc.splitTextToSize(line1, maxWidth)
        doc.text(wrapped, margin, y)
        y += lineHeight * wrapped.length
    })
    doc.text(line2, margin, y)
    y += lineHeight * 1.5
    doc.setTextColor(0, 0, 0)
    doc.text(doc.splitTextToSize(line3, maxWidth), margin, y)
    doc.save("Credit_Authorization.pdf")
}

export default function CreditReportV2({ changeStep }) {
    const history = useHistory()
    const loanId = window.location.pathname.replace("/app/borrower/", "").replace(/\/$/, "")

    const [application, setApplication] = useState(null)
    const [loadingApp, setLoadingApp] = useState(true)
    const [creditReports, setCreditReports] = useState([])
    const [selectedReportId, setSelectedReportId] = useState("")
    const [creditLiability, setCreditLiability] = useState([])
    const [loadingTable, setLoadingTable] = useState(true)
    const [reportSelectOptions, setReportSelectOptions] = useState([])

    const [accountOptions, setAccountOptions] = useState({ SoftPull: [], HardPull: [] })
    const [borrowerPaidPolicy, setBorrowerPaidPolicy] = useState(null)
    const [paymentPreference, setPaymentPreference] = useState(null)
    const [pendingPaymentRequest, setPendingPaymentRequest] = useState(null)
    const [paymentBannerLoading, setPaymentBannerLoading] = useState(false)
    const [hasTeamAccount, setHasTeamAccount] = useState(false)
    const [hasPersonalAccount, setHasPersonalAccount] = useState(false)

    const [isModalOpen, setIsModalOpen] = useState(false)
    const [pullScope, setPullScope] = useState("all")
    const [pullPartyId, setPullPartyId] = useState("")
    const [pullModeChoice, setPullModeChoice] = useState("joint")
    const [creditRadio, setCreditRadio] = useState("")
    const [selectAccount, setSelectAccount] = useState(null)
    const [checkLoading, setCheckLoading] = useState(false)

    const [consents, setConsents] = useState({})
    const [consentPartyId, setConsentPartyId] = useState("")
    const [isConsentOpen, setIsConsentOpen] = useState(false)
    const [consentType, setConsentType] = useState(undefined)
    const [openNotice, setOpenNotice] = useState(false)
    const undoConsentPartyIdRef = useRef("")
    const noticeTimerRef = useRef(null)

    const [excludeZeroBalance, setExcludeZeroBalance] = useState(true)
    const [excludeClosed, setExcludeClosed] = useState(true)
    const [errorData, setErrorData] = useState({ error: false, content: "" })
    const [reviewModal, setReviewModal] = useState(false)
    const [currentTable, setCurrentTable] = useState(null)
    const [isSwitch, setIsSwitch] = useState(false)
    const [creditFilter, setCreditFilter] = useState("")
    const [statusFilter, setStatusFilter] = useState("")
    const [currentDataSource, setCurrentDataSource] = useState([])
    const [creditLoanTypeOptions, setCreditLoanTypeOptions] = useState([])
    const [accountStatusOptions, setAccountStatusOptions] = useState([])

    const participants = useMemo(() => listParticipants(application), [application])
    const selectedReport = useMemo(
        () => creditReports.find((item) => `${item.id}` === `${selectedReportId}`) || null,
        [creditReports, selectedReportId],
    )
    // A report row exists for every pull, including ones the vendor never finished. Those
    // carry no borrowers, scores or PDF, so anything that consumes report data has to be
    // gated on the vendor's status rather than on the row merely existing.
    const selectedReportComplete = useMemo(() => isReportComplete(selectedReport), [selectedReport])
    const selectedReportPending = useMemo(() => isReportPending(selectedReport), [selectedReport])
    const selectedReportUsable = useMemo(() => isReportUsable(selectedReport), [selectedReport])
    const selectedReportVendorMessage = useMemo(() => reportVendorMessage(selectedReport), [selectedReport])
    const selectedReportBlockedReason = useMemo(() => {
        if (!selectedReport || selectedReportUsable) return undefined
        if (selectedReportPending) {
            return "The credit bureau has not finished this order yet. Use “Collect report” to check again."
        }
        if (!selectedReportComplete) {
            return "This credit pull did not complete, so there is no report data."
        }
        return selectedReportVendorMessage
            ? `The credit bureau returned no records — ${selectedReportVendorMessage}`
            : "The credit bureau returned no records for this borrower."
    }, [selectedReport, selectedReportComplete, selectedReportPending, selectedReportUsable, selectedReportVendorMessage])
    const [collectingReport, setCollectingReport] = useState(false)

    const parties = useMemo(
        () => buildCreditReportsTabParties(application, creditReports, selectedReportId),
        [application, creditReports, selectedReportId],
    )
    const ownershipReportContext = useMemo(
        () => (selectedReport && application
            ? resolveOwnershipReportContext(selectedReport, application)
            : null),
        [application, selectedReport],
    )
    const ownershipSelectOptions = useMemo(
        () => buildCreditOwnershipOptions(ownershipReportContext, application),
        [application, ownershipReportContext],
    )
    const ownershipEditable = isCreditOwnershipEditable(ownershipReportContext)
    const formatOwnershipLabel = useCallback((text, record) => (
        formatCreditOwnershipLabel(
            { ...record, ownership: text || record?.ownership },
            ownershipReportContext,
            application,
        )
    ), [application, ownershipReportContext])
    const pullSubjects = useMemo(() => {
        if (!pullPartyId || !application) return { pullMode: "individual", subjectPartyIds: [] }
        return resolvePullSubjects(pullPartyId, application, pullModeChoice)
    }, [pullPartyId, application, pullModeChoice])
    const canChoosePullMode = useMemo(
        () => !!(pullPartyId && application && canChooseCreditPullMode(pullPartyId, application)),
        [pullPartyId, application],
    )
    const spousePairNames = useMemo(() => {
        if (!application) return ""
        const primaryPartyId = application.primaryPartyId || application.participantOrder?.[0] || ""
        const cohabitingPartyId = (application.cohabitingPartyId || "").trim()
        return [primaryPartyId, cohabitingPartyId]
            .map((id) => participants.find((p) => p.partyId === id))
            .filter(Boolean)
            .map((p) => formatParticipantName(p))
            .join(" · ")
    }, [application, participants])
    const detailColumns = useMemo(() => ([
        {
            title: "Name",
            dataIndex: "creditorName",
            key: "name",
            render: (value) => <div style={{ color: "#222" }}>{value}</div>,
        },
        {
            title: "Remaining Balance",
            dataIndex: "unpaidBalanceAmount",
            key: "balance",
            render: (value) => <div style={{ color: "#222" }}>{formatMoney(value)}</div>,
        },
        {
            title: "Mo. pay",
            dataIndex: "monthlyPaymentAmount",
            key: "payment",
            render: (value) => <div style={{ color: "#222" }}>{formatMoney(value)}</div>,
        },
        {
            title: "Mo. left to pay",
            dataIndex: "monthsRemainingCount",
            key: "months",
            render: (value) => <div style={{ color: "#222" }}>{value}</div>,
        },
        {
            title: "Opened date",
            dataIndex: "openedDate",
            key: "date",
            render: (value) => <div style={{ color: "#222" }}>{value ? dayjs(value).format("MM/DD/YYYY") : "--"}</div>,
        },
        {
            title: "Credit loan type",
            dataIndex: "loanType",
            key: "loanType",
            render: (value) => <div style={{ color: "#222" }}>{camelCaseToSpaces(value)}</div>,
        },
        {
            title: "Acct status",
            dataIndex: "accountStatus",
            key: "status",
            render: (value) => <div style={{ color: "#222" }}>{value}</div>,
        },
    ]), [])

    const applySelectedReport = useCallback((report) => {
        if (!report) {
            setCreditLiability([])
            setErrorData({ error: false, content: "" })
            return
        }
        setCreditLiability(report.liabilities || [])
        if (report.errors?.length) {
            // A usable report can still carry errors: one bureau failed ("NO RECORD FOUND")
            // while the others delivered. Name the bureau so it reads as a gap, not a failure.
            setErrorData({
                error: true,
                partial: isReportUsable(report),
                content: report.errors
                    .map((e) => {
                        const text = String(e.errorMessageText || e.ErrorMessageText || "").trim()
                        const source = e.errorMessageCategoryCode || e.ErrorMessageCategoryCode
                        return text && source ? `${source}: ${text}` : text
                    })
                    .filter(Boolean)
                    .join("\n") || "Credit check failed",
            })
        } else {
            setErrorData({ error: false, content: "" })
        }
    }, [])

    const reloadReports = useCallback(async (preferredReportId) => {
        setLoadingTable(true)
        try {
            const data = await fetchCreditReports(loanId, 50)
            setCreditReports(data)
            setReportSelectOptions(buildReportSelectOptions(data))
            if (!data.length) {
                setSelectedReportId("")
                applySelectedReport(null)
                return
            }
            const nextId = preferredReportId && data.some((item) => `${item.id}` === `${preferredReportId}`)
                ? preferredReportId
                : data[0].id
            setSelectedReportId(nextId)
            applySelectedReport(data.find((item) => `${item.id}` === `${nextId}`) || data[0])
        } catch (err) {
            alert(err.message || "An error occurred while fetching credit report.")
        } finally {
            setLoadingTable(false)
        }
    }, [applySelectedReport, loanId])

    const reloadApplication = useCallback(async () => {
        setLoadingApp(true)
        try {
            const body = await getLosApplicationV2(loanId)
            const app = body.Application || null
            setApplication(app)
            const ps = listParticipants(app)
            setConsents(loadConsentState(loanId, ps))
        } catch (err) {
            console.error(err)
            alert(err.message || "Failed to load application.")
        } finally {
            setLoadingApp(false)
        }
    }, [loanId])

    const refreshPendingPayment = useCallback(async () => {
        if (!loanId) return
        try {
            const body = await getLosCreditPullPaymentRequestV2(loanId)
            setPendingPaymentRequest(body?.Request || body?.request || null)
        } catch (err) {
            console.error(err)
            setPendingPaymentRequest(null)
        }
    }, [loanId])

    useEffect(() => {
        reloadApplication()
        reloadReports()
        refreshPendingPayment()
        fetchCreditAccounts()
            .then((options) => {
                setAccountOptions({ SoftPull: options.SoftPull || [], HardPull: options.HardPull || [] })
                setBorrowerPaidPolicy(options.BorrowerPaidPolicy || null)
                setPaymentPreference(options.CreditPullPaymentPreference || null)
                setHasTeamAccount(
                    (options.SoftPull || []).some((a) => a.isAdminConfig)
                    || (options.HardPull || []).some((a) => a.isAdminConfig),
                )
            })
            .catch((err) => console.error(err))
        fetch("/los/getfeaturecustomization", {
            method: "GET",
            headers: { Authorization: "Bearer " + sessionStorage.getItem("ZeitroA"), Cache: "no-cache" },
        })
            .then((res) => res.json())
            .then((js) => {
                setHasPersonalAccount((js.CreditAccounts || []).length > 0)
                if (js.CreditPullPaymentPreference) {
                    setPaymentPreference(js.CreditPullPaymentPreference)
                }
            })
            .catch(() => {})
    }, [loanId, reloadApplication, reloadReports, refreshPendingPayment])

    useEffect(() => {
        const loanTypes = [...new Set((creditLiability || []).map((item) => item.loanType).filter(Boolean))]
        const statuses = [...new Set((creditLiability || []).map((item) => item.accountStatus).filter(Boolean))]
        setCreditLoanTypeOptions(loanTypes.map((item) => ({ label: camelCaseToSpaces(item), value: item })))
        setAccountStatusOptions(statuses.map((item) => ({ label: item, value: item })))
    }, [creditLiability])

    const buildFilteredDetailRows = useCallback((modelTitle, partyRole, zeroSwitch, loanType, accountStatus, sourceReportId) => {
        const repoKey = modelTitle && modelTitle.includes("FICORiskScoreClassic")
            ? REPOSITORY_BY_MODEL.FICORiskScoreClassic
            : REPOSITORY_BY_MODEL[modelTitle] || bureauNameFromModel(modelTitle)
        const sourceReport = creditReports.find((item) => `${item.id}` === `${sourceReportId}`)
        const liabilities = sourceReport?.liabilities || creditLiability || []
        return liabilities
            .filter((info) => (info.repositories || []).includes(repoKey))
            .filter((info) => (partyRole ? info.role === partyRole : true))
            .filter((info) => (loanType ? info.loanType === loanType : true))
            .filter((info) => (accountStatus ? info.accountStatus === accountStatus : true))
            .filter((info) => (zeroSwitch ? Number(info.monthlyPaymentAmount || 0) !== 0 : true))
            .map((item, idx) => ({ ...item, key: `${item.id || item.accountIdentifier || idx}-${idx}` }))
    }, [creditLiability, creditReports])

    const restoreCreditPullSelection = useCallback(() => {
        const prefs = loadCreditPullPrefs()
        const applied = applyCreditPullPrefs(accountOptions, prefs)
        if (applied.creditRadio) {
            setCreditRadio(applied.creditRadio)
            setSelectAccount(applied.selectAccount)
            return
        }
        // Keep whatever is already in state if prefs missing (same-session).
    }, [accountOptions])

    const persistCreditPullSelection = useCallback((radio, account) => {
        if (!radio) return
        saveCreditPullPrefs({
            creditRadio: radio,
            vendor: account?.Vendor ?? account?.vendor,
            username: account?.Username ?? account?.UserName ?? account?.username,
        })
    }, [])

    // If modal opens before accounts finish loading, fill Soft/Hard + account once ready.
    useEffect(() => {
        if (!isModalOpen) return
        const softLen = accountOptions?.SoftPull?.length || 0
        const hardLen = accountOptions?.HardPull?.length || 0
        if (!softLen && !hardLen) return

        const prefs = loadCreditPullPrefs()
        if (!creditRadio) {
            const applied = applyCreditPullPrefs(accountOptions, prefs)
            if (applied.creditRadio) {
                setCreditRadio(applied.creditRadio)
                setSelectAccount(applied.selectAccount)
            }
            return
        }
        if (!selectAccount) {
            const applied = applyCreditPullPrefs(
                accountOptions,
                prefs?.creditRadio === creditRadio
                    ? prefs
                    : { creditRadio, vendor: "", username: "" },
            )
            if (applied.selectAccount) {
                setSelectAccount(applied.selectAccount)
            }
        }
    }, [accountOptions, isModalOpen, creditRadio, selectAccount])

    const openPullModalAll = () => {
        setPullScope("all")
        setPullPartyId("")
        setPullModeChoice("joint")
        restoreCreditPullSelection()
        setIsModalOpen(true)
    }

    const openPullModal = (partyId) => {
        setPullScope("single")
        setPullPartyId(partyId)
        setPullModeChoice(canChooseCreditPullMode(partyId, application) ? "joint" : "individual")
        restoreCreditPullSelection()
        setIsModalOpen(true)
    }

    const subjectsForPull = useMemo(() => {
        if (pullScope === "all") {
            const all = participants
            const primaryPartyId = application?.primaryPartyId || application?.participantOrder?.[0] || ""
            const cohabitingPartyId = (application?.cohabitingPartyId || "").trim()
            if (!cohabitingPartyId) return all
            const primary = all.find((p) => p.partyId === primaryPartyId)
            const spouse = all.find((p) => p.partyId === cohabitingPartyId)
            const others = all.filter((p) => p.partyId !== primaryPartyId && p.partyId !== cohabitingPartyId)
            return [primary, spouse, ...others].filter(Boolean)
        }
        return pullSubjects.subjectPartyIds
            .map((id) => participants.find((p) => p.partyId === id))
            .filter(Boolean)
    }, [application, participants, pullScope, pullSubjects.subjectPartyIds])

    const allSubjectsConsented = subjectsForPull.length > 0
        && subjectsForPull.every((p) => consents[p.partyId]?.isConsent)

    const hasSpousePair = !!(application?.cohabitingPartyId || "").trim()
    const showConsentWarning = !allSubjectsConsented && (
        pullScope === "single"
            ? pullSubjects.pullMode !== "joint"
            : pullModeChoice !== "joint" || participants.length > 2
    )
    const showJointFootnote = hasSpousePair && (
        (pullScope === "all" && pullModeChoice === "joint")
        || (pullScope === "single" && pullSubjects.pullMode === "joint")
    )
    const showSpousePullModeChoice = (pullScope === "all" && hasSpousePair)
        || (pullScope === "single" && canChoosePullMode)

    const borrowerPaidActive = isBorrowerPaidHardPull(
        creditRadio,
        selectAccount,
        borrowerPaidPolicy,
        paymentPreference,
    )
    const borrowerPaidOps = useMemo(() => {
        if (!borrowerPaidActive || !application) return []
        return buildBorrowerPaidPaymentOps(application, pullScope, pullPartyId, pullModeChoice)
    }, [borrowerPaidActive, application, pullScope, pullPartyId, pullModeChoice])
    const borrowerPaidAmount = useMemo(
        () => estimateBorrowerPaidAmount(selectAccount, borrowerPaidOps),
        [selectAccount, borrowerPaidOps],
    )
    const borrowerPaidAmountLabel = formatPriceMoney(borrowerPaidAmount) || "$0.00"

    const pendingPaymentStatus = useMemo(() => {
        if (!pendingPaymentRequest) return null
        const lastError = `${paymentRequestField(pendingPaymentRequest, "lastError", "LastError") || ""}`.trim()
        const completed = (paymentRequestField(pendingPaymentRequest, "creditReportIds", "CreditReportIDs") || []).length
        const emailTo = `${paymentRequestField(pendingPaymentRequest, "emailTo", "EmailTo") || ""}`.trim()
        const emailSentAt = paymentRequestField(pendingPaymentRequest, "emailSentAt", "EmailSentAt")
        if (lastError) {
            return {
                kind: completed > 0 ? "partial_failure" : "full_failure",
                lastError,
                completed,
                emailTo,
            }
        }
        if (emailSentAt || emailTo) {
            return { kind: "awaiting", emailTo, completed }
        }
        return { kind: "awaiting", emailTo, completed }
    }, [pendingPaymentRequest])

    const paymentStatusSubtitle = pendingPaymentRequest
        ? formatPendingPaymentSubtitle(pendingPaymentRequest)
        : (borrowerPaidActive ? `Borrower paid · ${borrowerPaidAmountLabel}` : "")

    const sendPaymentLink = async (resend = false, override = null) => {
        const account = override?.account || selectAccount
        const ops = override?.ops || borrowerPaidOps
        if (!account || !ops.length) return
        setCheckLoading(true)
        try {
            const result = await postLosCreditPullPaymentRequestV2(loanId, {
                accountVendor: account.Vendor || account.vendor,
                accountUsername: account.Username || account.userName || account.username,
                ops,
                resend: !!resend,
            })
            if (result.AlreadySent && !resend) {
                await refreshPendingPayment()
                const rawSentAt = result.PriorEmailSentAt || result.Request?.emailSentAt
                const sentAt = rawSentAt
                    ? dayjs(rawSentAt).format("MMM D, YYYY h:mm A")
                    : "earlier"
                const pullSummary = result.PriorPullStatusSummary
                    || "No credit report has been pulled from this link yet."
                Modal.confirm({
                    title: "Payment link already sent",
                    content: (
                        <div>
                            <p style={{ marginBottom: 8 }}>
                                A payment link was already sent on <strong>{sentAt}</strong>.
                            </p>
                            <p style={{ marginBottom: 8 }}>{pullSummary}</p>
                            <p style={{ marginBottom: 0 }}>
                                Send a new link? The previous link will stop working. Hard credit may be pulled again if the borrower pays the new link.
                            </p>
                        </div>
                    ),
                    okText: "Send new link",
                    cancelText: "Cancel",
                    onOk: () => sendPaymentLink(true, override),
                })
                return
            }
            setIsModalOpen(false)
            persistCreditPullSelection(creditRadio, account)
            setPullPartyId("")
            setPullScope("all")
            await refreshPendingPayment()
            const primary = participants.find((p) => p.isPrimary) || participants[0]
            const recipientName = formatParticipantName(primary) || "Primary borrower"
            const recipientEmail = (result.Request?.emailTo
                || result.Request?.EmailTo
                || primary?.email
                || "").trim()
            Modal.success({
                title: "Payment link sent",
                content: recipientEmail
                    ? `${recipientName} (${recipientEmail}) will receive an email with a secure payment link.`
                    : `${recipientName} will receive an email with a secure payment link.`,
            })
        } catch (err) {
            await refreshPendingPayment()
            alert(err.message || "Failed to send payment link.")
        } finally {
            setCheckLoading(false)
        }
    }

    const resendPaymentLinkFromBanner = () => {
        if (!pendingPaymentRequest) return
        Modal.confirm({
            title: "Resend payment email?",
            content: "We will email the same payment link again. The borrower will not be charged twice for this request.",
            okText: "Resend email",
            cancelText: "Cancel",
            onOk: async () => {
                setPaymentBannerLoading(true)
                try {
                    await postLosCreditPullPaymentRequestV2(loanId, { resendEmailOnly: true })
                    await refreshPendingPayment()
                    Modal.success({
                        title: "Payment email resent",
                        content: "The borrower will receive another email with the same payment link.",
                    })
                } catch (err) {
                    await refreshPendingPayment()
                    alert(err.message || "Failed to resend payment email.")
                } finally {
                    setPaymentBannerLoading(false)
                }
            },
        })
    }

    const cancelPaymentRequestFromBanner = () => {
        if (!pendingPaymentRequest) return
        Modal.confirm({
            title: "Cancel payment request?",
            content: "The borrower payment link will stop working. You can send a new link later.",
            okText: "Cancel request",
            okButtonProps: { danger: true },
            cancelText: "Keep waiting",
            onOk: async () => {
                setPaymentBannerLoading(true)
                try {
                    await deleteLosCreditPullPaymentRequestV2(loanId)
                    setPendingPaymentRequest(null)
                } catch (err) {
                    alert(err.message || "Failed to cancel payment request.")
                } finally {
                    setPaymentBannerLoading(false)
                }
            },
        })
    }

    const handleProceedPull = async () => {
        const profileGaps = listCreditPullProfileGaps(subjectsForPull)
        if (profileGaps.length) {
            Modal.warning({
                title: "Missing borrower information",
                content: (
                    <div>
                        <p style={{ marginBottom: 8 }}>
                            Fill in SSN and Current residential address on the 1003 before sending a payment link or pulling credit.
                        </p>
                        <ul style={{ marginBottom: 0, paddingLeft: 20 }}>
                            {profileGaps.map((gap) => (
                                <li key={gap.partyId || gap.name}>
                                    <strong>{gap.name === "--" ? "Borrower" : gap.name}</strong>
                                    {`: ${gap.missing.join(", ")}`}
                                </li>
                            ))}
                        </ul>
                    </div>
                ),
            })
            return
        }
        if (borrowerPaidActive) {
            await sendPaymentLink(false)
            return
        }
        if (!selectAccount || !creditRadio) return
        if (pullScope === "single" && !pullPartyId) return
        if (pullScope === "all" && !participants.length) return
        setCheckLoading(true)
        try {
            const payloadBase = {
                reportType: REPORT_TYPE_BY_RADIO[creditRadio],
                account: {
                    vendor: selectAccount.Vendor,
                    userName: selectAccount.Username,
                    password: selectAccount.Password,
                },
            }
            let lastReportId
            if (pullScope === "all") {
                const ops = buildAllCreditPullOperations(application, pullModeChoice)
                for (const op of ops) {
                    const payload = { ...payloadBase }
                    if (op.pullMode === "joint" || op.pullMode === "individual") {
                        payload.pullMode = op.pullMode
                    }
                    const subjects = resolvePullSubjects(op.partyId, application, op.pullMode)
                    const subjectLabel = formatCreditPullSubjectsLabel(
                        subjects.subjectPartyIds,
                        application?.participantsById || participants,
                        subjects.pullMode,
                    )
                    try {
                        const result = await postLosParticipantCreditPullV2(loanId, op.partyId, payload)
                        lastReportId = result?.report?.id || lastReportId
                    } catch (err) {
                        throw withCreditPullSubjectError(err, subjectLabel)
                    }
                }
            } else {
                const payload = { ...payloadBase }
                if (canChoosePullMode) {
                    payload.pullMode = pullSubjects.pullMode
                }
                const subjectLabel = formatCreditPullSubjectsLabel(
                    pullSubjects.subjectPartyIds,
                    application?.participantsById || participants,
                    pullSubjects.pullMode,
                )
                try {
                    const result = await postLosParticipantCreditPullV2(loanId, pullPartyId, payload)
                    lastReportId = result?.report?.id
                } catch (err) {
                    throw withCreditPullSubjectError(err, subjectLabel)
                }
            }
            setIsModalOpen(false)
            persistCreditPullSelection(creditRadio, selectAccount)
            // Keep Soft/Hard + account for next open in this session.
            setPullPartyId("")
            setPullScope("all")
            await reloadReports(lastReportId)
            await reloadApplication()
        } catch (err) {
            alert(err.message || "An unexpected error occurred. Please try again later.")
        } finally {
            setCheckLoading(false)
        }
    }

    const handleOwnershipChange = async (record) => {
        if (!selectedReportId) return
        setLoadingTable(true)
        try {
            const token = sessionStorage.getItem("ZeitroA")
            const res = await fetch("/api/v1/credit/updateReport", {
                method: "PUT",
                headers: {
                    Authorization: "Bearer " + token,
                    Cache: "no-cache",
                    "Content-Type": "application/json",
                },
                body: JSON.stringify({ id: selectedReportId, liabilities: [record] }),
            })
            if (!res.ok) {
                alert("Failed to update ownership.")
                return
            }
            await reloadReports(selectedReportId)
        } catch {
            alert("An error occurred while updating ownership.")
        } finally {
            setLoadingTable(false)
        }
    }

    // Re-queries the vendor for an order that was still processing when the pull returned.
    // A status query on an existing order: no new credit inquiry, nothing billable.
    // `silent` is for the auto-collect below, which nobody asked for: it must not pop
    // alerts or drive the button's spinner. Returns the outcome so the caller can react.
    const collectPendingReport = useCallback(async ({ silent = false } = {}) => {
        if (!selectedReportId) return "skipped"
        if (!silent) setCollectingReport(true)
        try {
            const { pending } = await resumeCreditReport(selectedReportId, selectAccount
                ? { vendor: selectAccount.Vendor, userName: selectAccount.Username, password: selectAccount.Password }
                : undefined)
            if (pending) {
                if (!silent) alert("The credit bureau is still working on this order. Try again in a few minutes.")
                return "pending"
            }
            await reloadReports(selectedReportId)
            await reloadApplication()
            return "collected"
        } catch (err) {
            if (!silent) alert(err.message || "Failed to collect the credit report.")
            return "error"
        } finally {
            if (!silent) setCollectingReport(false)
        }
    }, [reloadApplication, reloadReports, selectAccount, selectedReportId])

    // While a pull is still processing, quietly re-check it so the report lands on its own
    // rather than waiting for someone to press "Collect report". This is exactly the query
    // that button makes, so it costs no new credit inquiry. It stops by itself: the report
    // arriving flips selectedReportPending and tears the interval down.
    const autoCollectTicks = useRef(0)
    const autoCollectBusy = useRef(false)
    useEffect(() => {
        autoCollectTicks.current = 0
    }, [selectedReportId])
    useEffect(() => {
        if (!selectedReportPending || !selectedReportId) return undefined
        const timer = setInterval(async () => {
            // Don't spend ticks on a tab nobody is looking at, and never stack requests.
            if (autoCollectBusy.current || document.visibilityState !== "visible") return
            if (autoCollectTicks.current >= AUTO_COLLECT_MAX_TICKS) return
            autoCollectTicks.current += 1
            autoCollectBusy.current = true
            try {
                // One refusal from the bureau (bad credentials, an order it will not talk
                // about) would be refused every 30s for the rest of the budget, so stop and
                // leave it to the button, which surfaces the actual message.
                if (await collectPendingReport({ silent: true }) === "error") {
                    autoCollectTicks.current = AUTO_COLLECT_MAX_TICKS
                }
            } finally {
                autoCollectBusy.current = false
            }
        }, AUTO_COLLECT_INTERVAL_MS)
        return () => clearInterval(timer)
    }, [collectPendingReport, selectedReportId, selectedReportPending])

    const downloadCreditReport = async () => {
        if (!selectedReportId) return
        try {
            const token = sessionStorage.getItem("ZeitroA")
            const response = await fetch("/api/v1/credit/downloadReport", {
                method: "POST",
                body: JSON.stringify({ id: selectedReportId, fileType: "pdf" }),
                headers: {
                    Authorization: "Bearer " + token,
                    Cache: "no-cache",
                    "Content-Type": "application/json",
                },
            })
            if (!response.ok) {
                // The server describes why (most often: the pull never completed, so there
                // is no PDF). Show that instead of saving the error body as a .pdf.
                const detail = await response.json().catch(() => null)
                alert(detail?.Text || detail?.errMessage || "Failed to download PDF.")
                return
            }
            // Guard against a 200 that is not actually a file.
            const contentType = response.headers.get("content-type") || ""
            if (contentType.includes("application/json")) {
                const detail = await response.json().catch(() => null)
                alert(detail?.errMessage || detail?.Text || "This report has no PDF file.")
                return
            }
            const blob = await response.blob()
            const link = document.createElement("a")
            link.href = URL.createObjectURL(blob)
            link.download = "Credit_Report.pdf"
            document.body.appendChild(link)
            link.click()
            document.body.removeChild(link)
            URL.revokeObjectURL(link.href)
        } catch {
            alert("An error occurred while downloading the credit report.")
        }
    }

    const confirmConsent = () => {
        if (!consentPartyId || !consentType) return
        undoConsentPartyIdRef.current = consentPartyId
        setConsents((prev) => {
            const next = {
                ...prev,
                [consentPartyId]: { isConsent: true, isConsentType: consentType },
            }
            saveConsentState(loanId, next)
            return next
        })
        setIsConsentOpen(false)
        setConsentType(undefined)
        setConsentPartyId("")
        setOpenNotice(true)
        if (noticeTimerRef.current) clearTimeout(noticeTimerRef.current)
        noticeTimerRef.current = setTimeout(() => setOpenNotice(false), 5000)
    }

    const undoConsent = () => {
        const partyId = undoConsentPartyIdRef.current
        if (partyId) {
            setConsents((prev) => {
                const next = {
                    ...prev,
                    [partyId]: { isConsent: false, isConsentType: "" },
                }
                saveConsentState(loanId, next)
                return next
            })
            undoConsentPartyIdRef.current = ""
        }
        setOpenNotice(false)
        if (noticeTimerRef.current) {
            clearTimeout(noticeTimerRef.current)
            noticeTimerRef.current = null
        }
    }

    const downloadCreditAuthorization = () => {
        const acceptances = participants
            .filter((p) => p?.creditconsented)
            .map((p) => ({
                name: formatParticipantName(p),
                email: p?.email || "",
                consentDate: formatCreditConsentDate(p?.creditconsentdate),
            }))
        if (acceptances.length === 0) return
        buildCreditAuthorizationPdf({
            acceptances,
            companyName: application?.brokerinfo?.name || application?.brokerInfo?.name || application?.companyName || "",
        })
    }

    const consentedParticipants = useMemo(
        () => participants.filter((p) => p?.creditconsented),
        [participants],
    )

    useEffect(() => () => {
        if (noticeTimerRef.current) clearTimeout(noticeTimerRef.current)
    }, [])

    const canCheckCredit = hasTeamAccount || hasPersonalAccount

    return (
        <div style={{ padding: "18px 36px" }}>
            <div style={{ fontFamily: "Inter", fontSize: 20, fontWeight: 600, color: "#000", marginBottom: 12, lineHeight: "26px" }}>
                Credit report
            </div>

            {(paymentStatusSubtitle || pendingPaymentStatus) && (
                <div className="credit-payment-status-block">
                    {paymentStatusSubtitle && (
                        <div className="credit-payment-status-subtitle">
                            Credit pull payment: <strong>{paymentStatusSubtitle}</strong>
                        </div>
                    )}
                    {pendingPaymentStatus && (
                        <div
                            className={`credit-payment-status-banner ${
                                pendingPaymentStatus.kind === "awaiting"
                                    ? "is-awaiting"
                                    : (pendingPaymentStatus.kind === "partial_failure" ? "is-partial" : "is-failed")
                            }`}
                        >
                            <div className="credit-payment-status-main">
                                <span className="credit-payment-status-dot" aria-hidden />
                                <span className="credit-payment-status-title">
                                    {pendingPaymentStatus.kind === "awaiting"
                                        ? "Awaiting payment"
                                        : (pendingPaymentStatus.kind === "partial_failure"
                                            ? "Partial payment failure"
                                            : "Payment failed")}
                                </span>
                                <span className="credit-payment-status-msg">
                                    {pendingPaymentStatus.kind === "awaiting"
                                        ? (pendingPaymentStatus.emailTo
                                            ? `Link sent to ${pendingPaymentStatus.emailTo}`
                                            : "Payment link sent to the borrower.")
                                        : (
                                            <>
                                                {friendlyPaymentLastError(pendingPaymentStatus.lastError)}
                                                {" · the borrower can retry from the same link. "}
                                                {pendingPaymentStatus.kind === "partial_failure"
                                                    ? `${pendingPaymentStatus.completed} credit report(s) already pulled; remaining pulls will not run until payment succeeds.`
                                                    : "This account requires payment on all orders — the pull will not run until payment succeeds."}
                                            </>
                                        )}
                                </span>
                            </div>
                            <div className="credit-payment-status-actions">
                                <button
                                    type="button"
                                    className="credit-payment-status-resend"
                                    disabled={paymentBannerLoading || checkLoading}
                                    onClick={resendPaymentLinkFromBanner}
                                >
                                    Resend link
                                </button>
                                <button
                                    type="button"
                                    className="credit-payment-status-cancel"
                                    disabled={paymentBannerLoading || checkLoading}
                                    onClick={cancelPaymentRequestFromBanner}
                                >
                                    Cancel
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            )}

            {!canCheckCredit && (
                <div className="setUpAccount">
                    <div>
                        <div style={{ color: "#222", fontSize: 16, fontWeight: 600, marginBottom: 6 }}>No account connected</div>
                        <div style={{ color: "#222", fontSize: 14 }}>
                            {checkAccess([UR_Owner])
                                ? "Your credit account has not been set up. Please proceed to set up your team credit account in the management page, or set up your individual credit account in account settings."
                                : "The credit account has not been set up. Please contact your admin to set up the team credit account, or set up your individual credit account in account settings."}
                        </div>
                    </div>
                    <Button
                        className="credit-account-connect-btn"
                        onClick={() => {
                            if (checkAccess([UR_Owner])) window.open("/app/management", "_self")
                        }}
                    >
                        connect now
                    </Button>
                </div>
            )}

            <Row className="credit-report-top-bar">
                <Col span={24} sm={12}>
                    <Select
                        className="check-date-select"
                        value={selectedReportId || undefined}
                        onChange={(value) => {
                            setSelectedReportId(value)
                            setCurrentTable(null)
                            setCurrentDataSource([])
                            setCreditFilter("")
                            setStatusFilter("")
                            setIsSwitch(false)
                            applySelectedReport(creditReports.find((item) => `${item.id}` === `${value}`))
                        }}
                        options={reportSelectOptions}
                        placeholder="Select credit check date"
                    />
                </Col>
                <Col span={24} sm={12}>
                    <div style={{ display: "flex", justifyContent: "flex-end", gap: 16 }}>
                        {selectedReportPending && (
                            <Button
                                className="credit-report-btn"
                                loading={collectingReport}
                                onClick={() => collectPendingReport()}
                            >
                                Collect report
                            </Button>
                        )}
                        <Tooltip title={
                            !creditReports.length
                                ? "Pull a credit report first"
                                : (!selectedReportComplete ? selectedReportBlockedReason : undefined)
                        }>
                            <Button
                                className="credit-report-btn credit-report-import"
                                type="primary"
                                disabled={!selectedReport || !creditReports.length || !selectedReportComplete}
                                onClick={() => {
                                    track(STATSIG.LOAN.IMPORT_FROM_CREDIT_REPORT, 'click_open_popup', {
                                        type: 'click_open_popup',
                                    })
                                    setReviewModal(true)
                                }}
                            >
                                Import to 1003
                            </Button>
                        </Tooltip>
                        <Tooltip title={
                            creditReports.length && !selectedReportComplete ? selectedReportBlockedReason : undefined
                        }>
                            <Button
                                className="credit-report-btn credit-report-download"
                                type="primary"
                                disabled={!creditReports.length || !selectedReportComplete}
                                onClick={downloadCreditReport}
                            >
                                Download
                            </Button>
                        </Tooltip>
                        {canCheckCredit ? (
                            <Button
                                type="primary"
                                className="credit-report-btn credit-report-check"
                                disabled={!participants.length}
                                onClick={() => openPullModalAll()}
                            >
                                Check credit
                            </Button>
                        ) : (
                            <Button disabled className="credit-report-btn credit-report-check">Check credit</Button>
                        )}
                    </div>
                </Col>
            </Row>

            <Tabs className="credit-report-tabs" defaultActiveKey="CreditSummary">
                <TabPane tab="Credit summary" key="CreditSummary">
                    <div className="credit-summary-card">
                        <div className="credit-summary-card-title">
                            <img src={(selectedReport?.borrowers?.length || 0) > 1 ? "/images/double-person.svg" : "/images/single-person.svg"} alt="" />
                            <div className="credit-summary-card-title-text">
                                {selectedReport ? reportSummaryTitle(selectedReport) : "Individual credit summary"}
                            </div>
                        </div>
                        {selectedReport && !selectedReportUsable && (
                            <Alert
                                style={{ marginTop: 12 }}
                                type={selectedReportPending ? "warning" : "error"}
                                showIcon
                                message={selectedReportPending
                                    ? "This credit pull is still processing."
                                    : `This credit pull returned no report data — ${reportStatusLabel(selectedReport)}.`}
                                description={selectedReportPending
                                    ? "The credit bureau accepted the order but had not finished it when the request returned. Use “Collect report” above to fetch it — that re-checks the same order and does not pull credit again."
                                    : selectedReportBlockedReason}
                            />
                        )}
                        <div style={{ marginTop: 8, borderTop: "1px solid #ECECEC" }}>
                            {participants.map((participant, idx) => (
                                <div
                                    key={participant.partyId}
                                    style={{
                                        marginTop: idx === 0 ? 10 : 12,
                                        padding: "12px 14px 10px 14px",
                                        border: "1px solid #E8ECF2",
                                        borderRadius: 10,
                                        background: idx % 2 === 0 ? "#FFFFFF" : "#FAFBFD",
                                        borderLeft: participant.isPrimary ? "4px solid #325CEB" : "4px solid #D8DEE9",
                                    }}
                                >
                                    <div
                                        style={{
                                            display: "grid",
                                            gridTemplateColumns: "1.5fr 1fr 1fr 1.4fr",
                                            columnGap: 20,
                                            rowGap: 4,
                                            alignItems: "start",
                                        }}
                                    >
                                        <div>
                                            <div style={{ fontSize: 13, color: "#586570", fontWeight: 600 }}>
                                                {participant.isPrimary ? "Borrower" : "Co-borrower"}
                                                {application?.cohabitingPartyId === participant.partyId ? " (Spouse)" : ""}
                                            </div>
                                            <div style={{ fontSize: 15, color: "#222" }}>{formatParticipantName(participant)}</div>
                                        </div>
                                        <div>
                                            <div style={{ fontSize: 13, color: "#586570", fontWeight: 600 }}>SSN</div>
                                            <div style={{ fontSize: 15, color: "#222" }}>{maskSSN(participant?.socialSecurity)}</div>
                                        </div>
                                        <div>
                                            <div style={{ fontSize: 13, color: "#586570", fontWeight: 600 }}>Date of birth</div>
                                            <div style={{ fontSize: 15, color: "#222" }}>
                                                {participant?.dateOfBirth ? dayjs(participant.dateOfBirth).format("MM/DD/YYYY") : "--"}
                                            </div>
                                        </div>
                                        <div>
                                            <div style={{ fontSize: 13, color: "#586570", fontWeight: 600 }}>Address</div>
                                            <div style={{ fontSize: 15, color: "#222" }}>{formatParticipantAddress(participant)}</div>
                                        </div>
                                    </div>
                                    <div
                                        style={{
                                            marginTop: 10,
                                            paddingTop: 10,
                                            borderTop: "1px dashed #E6EAF0",
                                            display: "flex",
                                            gap: 8,
                                            alignItems: "center",
                                            flexWrap: "wrap",
                                        }}
                                    >
                                        <div
                                            style={consents[participant.partyId]?.isConsent
                                                ? { backgroundColor: "#D4F8D3", color: "#138D2E", borderRadius: 8, padding: "4px 10px", fontSize: 13 }
                                                : { backgroundColor: "#F8E8D3", color: "#CB8600", borderRadius: 8, padding: "4px 10px", fontSize: 13 }}
                                        >
                                            {consents[participant.partyId]?.isConsent ? "Credit consent confirmed" : "Credit consent pending"}
                                        </div>
                                        {!consents[participant.partyId]?.isConsent && (
                                            <Button
                                                type="link"
                                                size="small"
                                                style={{ paddingLeft: 6, paddingRight: 6 }}
                                                onClick={() => { setConsentPartyId(participant.partyId); setIsConsentOpen(true) }}
                                            >
                                                Consent confirmation
                                            </Button>
                                        )}
                                        <Button
                                            size="small"
                                            type="primary"
                                            style={{ borderRadius: 6 }}
                                            disabled={!canCheckCredit}
                                            onClick={() => openPullModal(participant.partyId)}
                                        >
                                            Pull for {participant.firstName || "participant"}
                                        </Button>
                                    </div>
                                </div>
                            ))}
                        </div>
                        {consentedParticipants.length > 0 && (
                            <div
                                style={{
                                    fontSize: 14,
                                    color: "#586570",
                                    marginTop: 12,
                                    paddingTop: 12,
                                    borderTop: "1px solid #ECECEC",
                                    display: "flex",
                                    flexDirection: "column",
                                    gap: 4,
                                    lineHeight: "22px",
                                }}
                            >
                                {consentedParticipants.map((p) => (
                                    <div key={p.partyId}>
                                        {formatParticipantName(p)} has consented to the credit check on {formatCreditConsentDate(p.creditconsentdate)}.
                                    </div>
                                ))}
                                <a
                                    href="#"
                                    onClick={(e) => { e.preventDefault(); downloadCreditAuthorization() }}
                                    style={{ color: "#1890ff", marginTop: 4, alignSelf: "flex-start" }}
                                >
                                    Download credit authorization
                                </a>
                            </div>
                        )}
                    </div>
                    <CreditSummaryTableV2
                        accounts={creditLiability}
                        loading={loadingTable || loadingApp}
                        onExcludeZeroBalanceChange={setExcludeZeroBalance}
                        onExcludeClosedChange={setExcludeClosed}
                        onOwnershipChange={handleOwnershipChange}
                        ownershipOptions={ownershipSelectOptions.length ? ownershipSelectOptions : undefined}
                        formatOwnershipLabel={ownershipReportContext && !ownershipReportContext.error
                            ? formatOwnershipLabel
                            : undefined}
                        ownershipEditable={ownershipEditable}
                    />
                    <CreditSummaryReviewV2
                        open={reviewModal}
                        loanId={loanId}
                        report={selectedReport}
                        application={application}
                        accounts={creditLiability}
                        initialExcludeZeroBalance={excludeZeroBalance}
                        initialExcludeClosed={excludeClosed}
                        loading={loadingTable || loadingApp}
                        defaultCheckedType="all"
                        ownershipReportContext={ownershipReportContext}
                        onCancel={() => setReviewModal(false)}
                        onComplete={(payload) => {
                            setReviewModal(false)
                            if (!changeStep) return
                            const link = typeof payload === "string" ? payload : payload?.link
                            const partyId = typeof payload === "string" ? "" : payload?.partyId
                            const primaryPartyId = application?.primaryPartyId
                            const from = partyId && partyId !== primaryPartyId ? "coborrower" : "borrower"
                            changeStep("1003View", from, link, partyId)
                        }}
                    />
                </TabPane>
                <TabPane tab="Credit reports" key="CreditReports">
                    {errorData.error && (
                        <>
                            <div style={{ color: "#CB8600", display: "flex", alignItems: "center", marginTop: 10, fontWeight: 500 }}>
                                <ExclamationCircleFilled />
                                <span style={{ marginLeft: 10 }}>
                                    {errorData.partial
                                        ? "Some credit bureaus did not return data — this report is incomplete"
                                        : "Credit check unavailable"}
                                </span>
                            </div>
                            <Alert
                                style={{ marginTop: 10 }}
                                type="warning"
                                message={<div style={{ whiteSpace: "pre-line" }}>{errorData.content}</div>}
                            />
                        </>
                    )}
                    {parties.map((partiesItem, partiesIndex) => {
                        const newCredit = partiesItem.credit.length ? partiesItem.credit : partiesReplace
                        const sourceReportId = partiesItem.sourceReportId
                        return (
                            <Space key={partiesItem.partyId || partiesItem.name || partiesIndex} direction="vertical" size="middle" style={{ display: "flex", marginTop: 24 }}>
                                <div style={{ fontFamily: "Inter", color: "#222", fontSize: 16, fontWeight: 500 }}>{partiesItem.name}</div>
                                {newCredit.map((item, index) => {
                                    const panelKey = `${partiesItem.partyId || partiesIndex}-${index}`
                                    const expanded = currentTable === panelKey
                                    return (
                                        <Card key={panelKey} className="credit-history-card">
                                            <div style={{ display: "flex", justifyContent: "space-between", gap: 36, padding: "0 18px" }}>
                                                <div>
                                                    <div style={{ height: 30, marginBottom: 9 }}>
                                                        {bureauLogoFromModel(item.bureau || item.title)}
                                                    </div>
                                                    <div className="reportScore">Score</div>
                                                    <div className="reportValue" style={item.value === "" || item.notScored ? { color: "#DBDCDE" } : {}}>
                                                        {item.value === "" ? "---" : item.notScored ? "N/A" : item.value}
                                                    </div>
                                                </div>
                                                <div style={{ width: "100%" }}>
                                                    <div style={{ fontWeight: 500, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                                                        <span>
                                                            {item.bureau || bureauNameFromModel(item.title)}
                                                            {item.bureau && item.title && item.title !== item.bureau && (
                                                                <span style={{ marginLeft: 8, color: "#8A8C90", fontWeight: 400 }}>{item.title}</span>
                                                            )}
                                                        </span>
                                                        <div
                                                            style={{ cursor: "pointer", height: 24, width: 24, display: "flex", alignItems: "center", justifyContent: "center" }}
                                                            onClick={() => {
                                                                if (expanded) {
                                                                    setCurrentTable(null)
                                                                } else {
                                                                    setCreditFilter("")
                                                                    setStatusFilter("")
                                                                    setIsSwitch(false)
                                                                    setCurrentTable(panelKey)
                                                                    setCurrentDataSource(buildFilteredDetailRows(
                                                                        item.bureau || item.title,
                                                                        partiesItem.role,
                                                                        false,
                                                                        "",
                                                                        "",
                                                                        sourceReportId,
                                                                    ))
                                                                }
                                                            }}
                                                        >
                                                            {expanded ? <UpOutlined /> : <DownOutlined />}
                                                        </div>
                                                    </div>
                                                    {item.factors.length > 0 ? (
                                                        <div className="factorScore">
                                                            <div className="FactorsTitle">Factors Influencing Credit Score:</div>
                                                            <ul style={{ paddingLeft: "24px", color: "rgba(34, 34, 34, 0.90)", fontSize: "14px", margin: "4px 0 0", lineHeight: "18px" }}>
                                                                {item.factors.map((factor) => <li key={factor}>{factor}</li>)}
                                                            </ul>
                                                        </div>
                                                    ) : (
                                                        <div className="factorScore" style={{ textAlign: "center", height: 64, display: "flex", alignItems: "center", justifyContent: "center" }}>
                                                            <div style={{ color: "#6E6E70", fontSize: "16px", lineHeight: "20px" }}>Credit not pulled</div>
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                            {expanded && (
                                                <div style={{ marginTop: 10 }}>
                                                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "0 18px" }}>
                                                        <div style={{ display: "flex", alignItems: "center" }}>
                                                            <Switch
                                                                value={isSwitch}
                                                                onChange={(value) => {
                                                                    setIsSwitch(value)
                                                                    setCurrentDataSource(buildFilteredDetailRows(
                                                                        item.bureau || item.title,
                                                                        partiesItem.role,
                                                                        value,
                                                                        creditFilter,
                                                                        statusFilter,
                                                                        sourceReportId,
                                                                    ))
                                                                }}
                                                            />
                                                            <div style={{ fontFamily: "Inter", fontSize: 16, color: "#222", lineHeight: "normal", paddingLeft: 10 }}>Exclude 0 monthly payment records</div>
                                                        </div>
                                                        <div>
                                                            <Select
                                                                className="credit-history-select"
                                                                placeholder="Credit loan type"
                                                                allowClear
                                                                value={creditFilter || undefined}
                                                                onChange={(value) => {
                                                                    const next = value || ""
                                                                    setCreditFilter(next)
                                                                    setCurrentDataSource(buildFilteredDetailRows(
                                                                        item.bureau || item.title,
                                                                        partiesItem.role,
                                                                        isSwitch,
                                                                        next,
                                                                        statusFilter,
                                                                        sourceReportId,
                                                                    ))
                                                                }}
                                                                style={{ width: "250px", height: "38px", marginRight: "16px" }}
                                                                options={creditLoanTypeOptions}
                                                            />
                                                            <Select
                                                                className="credit-history-select"
                                                                allowClear
                                                                value={statusFilter || undefined}
                                                                onChange={(value) => {
                                                                    const next = value || ""
                                                                    setStatusFilter(next)
                                                                    setCurrentDataSource(buildFilteredDetailRows(
                                                                        item.bureau || item.title,
                                                                        partiesItem.role,
                                                                        isSwitch,
                                                                        creditFilter,
                                                                        next,
                                                                        sourceReportId,
                                                                    ))
                                                                }}
                                                                placeholder="Status"
                                                                style={{ width: "150px", height: "38px" }}
                                                                options={accountStatusOptions}
                                                            />
                                                        </div>
                                                    </div>
                                                    <Table className="credit-history-table" style={{ marginTop: "10px" }} columns={detailColumns} dataSource={currentDataSource} />
                                                </div>
                                            )}
                                        </Card>
                                    )
                                })}
                            </Space>
                        )
                    })}
                </TabPane>
            </Tabs>

            <Modal
                className="credit-check-modal"
                destroyOnClose
                width={976}
                open={isModalOpen}
                title="Credit check"
                onCancel={() => {
                    setIsModalOpen(false)
                    // Do not clear Soft/Hard/account — preserve last choice for next open.
                    if (creditRadio) {
                        persistCreditPullSelection(creditRadio, selectAccount)
                    }
                    setPullPartyId("")
                    setPullScope("all")
                    setPullModeChoice("joint")
                }}
                footer={(
                    <div className="btn-wrap">
                        <div />
                        <Button
                            className="credit-check-btn"
                            type="primary"
                            disabled={
                                !allSubjectsConsented
                                || !selectAccount
                                || !creditRadio
                                || (borrowerPaidActive && (!borrowerPaidOps.length || borrowerPaidAmount <= 0))
                            }
                            loading={checkLoading}
                            onClick={handleProceedPull}
                        >
                            {borrowerPaidActive ? "Send payment link" : "Proceed with credit check"}
                        </Button>
                    </div>
                )}
            >
                <div className="borrowerContent">
                    {borrowerPaidActive && (
                        <div className="borrower-paid-banner">
                            <div className="borrower-paid-banner-icon" aria-hidden>
                                <LinkOutlined />
                            </div>
                            <div className="borrower-paid-banner-copy">
                                <div className="borrower-paid-banner-title">
                                    Borrower paid · {borrowerPaidAmountLabel}
                                </div>
                                <div className="borrower-paid-banner-desc">
                                    Your setting in Settings → Credit report. The borrower sees this amount at checkout and the report pulls automatically after payment.
                                </div>
                            </div>
                            <button
                                type="button"
                                className="borrower-paid-banner-settings"
                                onClick={() => {
                                    setIsModalOpen(false)
                                    history.push({
                                        pathname: "/app/creditpullsetting",
                                        state: {
                                            selected: "creditReport",
                                            creditReportTab: "HardPull",
                                        },
                                    })
                                }}
                            >
                                Settings
                            </button>
                        </div>
                    )}
                    {showSpousePullModeChoice && (
                        <div style={{ marginBottom: 24 }}>
                            <div className="credit-check-title">
                                {pullScope === "all" ? "Married co-borrowers" : "Pull for"}
                            </div>
                            <Radio.Group
                                style={{ display: "flex", flexDirection: "column", gap: 12, marginTop: 8 }}
                                value={pullModeChoice}
                                onChange={(e) => setPullModeChoice(e.target.value)}
                            >
                                <Radio value="joint">
                                    {`Joint credit check (${spousePairNames})`}
                                </Radio>
                                <Radio value="individual">
                                    {pullScope === "all"
                                        ? "Individual credit check (pull each spouse separately)"
                                        : `Individual credit check (${formatParticipantName(participants.find((p) => p.partyId === pullPartyId))} only)`}
                                </Radio>
                            </Radio.Group>
                        </div>
                    )}
                    {pullScope === "single" && !canChoosePullMode && (
                        <div style={{ marginBottom: 24 }}>
                            <div className="credit-check-title">Pull for</div>
                            <div style={{ fontSize: 16, color: "#222" }}>
                                {subjectsForPull.map((p) => formatParticipantName(p)).join(" · ")}
                                {" (Individual)"}
                            </div>
                        </div>
                    )}
                    <div style={{ marginBottom: 40 }}>
                        <div className="credit-check-title">How would you like to check credit?</div>
                        <Radio.Group
                            style={{ display: "flex" }}
                            value={creditRadio}
                            onChange={(e) => {
                                const selectedType = e.target.value
                                setCreditRadio(selectedType)
                                const accounts = accountOptions[selectedType] || []
                                const prefs = loadCreditPullPrefs()
                                let nextAccount = null
                                if (prefs?.creditRadio === selectedType) {
                                    nextAccount = applyCreditPullPrefs(
                                        { [selectedType]: accounts },
                                        { ...prefs, creditRadio: selectedType },
                                    ).selectAccount
                                }
                                if (!nextAccount) {
                                    const defaultIndex = accounts.findIndex((acc) => acc.IsDefault)
                                    if (defaultIndex >= 0) {
                                        nextAccount = { ...accounts[defaultIndex], index: defaultIndex }
                                    } else if (accounts[0]) {
                                        nextAccount = { ...accounts[0], index: 0 }
                                    }
                                }
                                setSelectAccount(nextAccount)
                                persistCreditPullSelection(selectedType, nextAccount)
                            }}
                        >
                            <label className="credit-check-select-radio" style={creditRadio === "SoftPull" ? { borderColor: "#325CEB" } : {}}>
                                <Radio value="SoftPull">Soft credit check</Radio>
                            </label>
                            <label className="credit-check-select-radio" style={creditRadio === "HardPull" ? { borderColor: "#325CEB" } : {}}>
                                <Radio value="HardPull">Hard credit check</Radio>
                            </label>
                        </Radio.Group>
                        {creditRadio !== "" && (
                            <div className="credit-account-select-wrap" style={{ marginTop: 20 }}>
                                <div className="credit-check-select-label">Select a credit account</div>
                                <Select
                                    style={{ width: "100%", height: 50 }}
                                    className="credit-account-select"
                                    getPopupContainer={(triggerNode) => triggerNode.parentElement}
                                    suffixIcon={<img src="/images/auto-reminder-select-arrow.svg" alt="" />}
                                    value={selectAccount ? `${creditRadio}-${selectAccount.index}` : undefined}
                                    placeholder="Select credit account"
                                    optionLabelProp="label"
                                    onChange={(value) => {
                                        const index = parseInt(value.split("-")[1], 10)
                                        const account = accountOptions[creditRadio][index]
                                        const next = { ...account, index }
                                        setSelectAccount(next)
                                        persistCreditPullSelection(creditRadio, next)
                                    }}
                                >
                                    {(accountOptions[creditRadio] || []).map((option, index) => (
                                        <Option
                                            key={index}
                                            value={`${creditRadio}-${index}`}
                                            label={(
                                                <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
                                                    <img src="/images/credit-account-profile.svg" alt="" />
                                                    <div style={{ fontFamily: "Inter", fontSize: 16, color: "#222", lineHeight: "20px" }}>
                                                        {VENDORS_LIST[option.Vendor] || option.Vendor} - {option.Username}
                                                    </div>
                                                    {option.IsDefault ? <div className="credit-account-default-tag">Default</div> : null}
                                                </span>
                                            )}
                                        >
                                            <span style={{ display: "flex", alignItems: "center", height: 38, gap: 8 }}>
                                                <div style={{ marginLeft: 8, fontFamily: "Inter", fontSize: 16, color: "#222", lineHeight: "20px" }}>
                                                    {VENDORS_LIST[option.Vendor] || option.Vendor} - {option.Username}
                                                </div>
                                                {option.IsDefault ? <div className="credit-account-default-tag">Default</div> : null}
                                            </span>
                                        </Option>
                                    ))}
                                </Select>
                            </div>
                        )}
                    </div>
                    <div className="credit-check-title">Borrowers</div>
                    {showConsentWarning && (
                        <div className="credit-check-consented-title">
                            The following borrower(s) hasn&apos;t consented to access their personal credit information yet, so they will not be pulled.
                        </div>
                    )}
                    {subjectsForPull.map((participant, index) => {
                        const isConsented = !!consents[participant.partyId]?.isConsent
                        let isJointPair
                        let isFirstJoint
                        let isSecondJoint
                        if (pullScope === "all") {
                            if (pullModeChoice === "joint") {
                                ({ isJointPair, isFirstJoint, isSecondJoint } = resolveSpousePairJointCardFlags(
                                    participant,
                                    index,
                                    subjectsForPull,
                                    application,
                                ))
                            } else {
                                isJointPair = false
                                isFirstJoint = false
                                isSecondJoint = false
                            }
                        } else {
                            isJointPair = pullSubjects.pullMode === "joint" && subjectsForPull.length === 2
                            isFirstJoint = isJointPair && index === 0
                            isSecondJoint = isJointPair && index === 1
                        }
                        return (
                            <div
                                key={participant.partyId}
                                className="credit-check-borrowers-box"
                                style={{
                                    border: !isConsented && !isJointPair ? "1px solid #CB8600" : "1px solid #ECECEC",
                                    borderBottom: isFirstJoint ? "none" : "1px solid #ECECEC",
                                    borderTop: isSecondJoint ? "none" : undefined,
                                    borderBottomLeftRadius: isFirstJoint ? 0 : 8,
                                    borderBottomRightRadius: isFirstJoint ? 0 : 8,
                                    borderTopLeftRadius: isSecondJoint ? 0 : 8,
                                    borderTopRightRadius: isSecondJoint ? 0 : 8,
                                    marginBottom: isFirstJoint ? 0 : 16,
                                    paddingBottom: isFirstJoint ? 0 : 20,
                                }}
                            >
                                <div className="credit-check-borrowers-top">
                                    <div className="personal-information">
                                        <span>{formatParticipantName(participant)}</span>
                                        <span style={{ fontWeight: 400 }}> · SSN {maskSSN(participant.socialSecurity)}</span>
                                    </div>
                                    <div
                                        style={isConsented
                                            ? { backgroundColor: "#D4F8D3", color: "#138D2E" }
                                            : { backgroundColor: "#F8E8D3", color: "#CB8600" }}
                                        className="credit-consent-status"
                                    >
                                        <img src={`/images/credit-${isConsented ? "confirmed" : "unconfirmed"}.svg`} alt="" />
                                        <span>Credit consent</span>
                                    </div>
                                </div>
                                <div style={{ color: "#586570", marginTop: 15 }}>
                                    <div className="credit-check-info-item">
                                        <div className="info-item-label">Date of birth:</div>
                                        <div>{formatParticipantDob(participant.dateOfBirth)}</div>
                                    </div>
                                    <div className="credit-check-info-item">
                                        <div className="info-item-label">Current address:</div>
                                        <div>{formatParticipantAddress(participant)}</div>
                                    </div>
                                </div>
                                <div className="credit-check-borrowers-bottom">
                                    {!isConsented && (
                                        <div
                                            className="credit-consent-btn"
                                            onClick={() => { setConsentPartyId(participant.partyId); setIsConsentOpen(true) }}
                                        >
                                            Consent confirmation
                                        </div>
                                    )}
                                </div>
                                {isFirstJoint ? <div style={{ height: 1, backgroundColor: "#ECECEC", width: "100%", marginTop: 20 }} /> : null}
                            </div>
                        )
                    })}
                    {showJointFootnote && (
                        <div style={{ fontFamily: "Inter", fontSize: 16, color: "#707070", lineHeight: "20px" }}>
                            *A <span style={{ textDecorationLine: "underline" }}>joint credit pull</span> will be pulled for married co-borrowers.
                        </div>
                    )}
                </div>
            </Modal>

            {openNotice && (
                <div className="consent-confirmed-notice credit-consent-toast showNotice">
                    <div>Credit consent confirmed</div>
                    <div className="consent-confirmed-notice-btn" onClick={undoConsent}>Undo</div>
                    <div className="consent-confirmed-notice-close" onClick={() => setOpenNotice(false)}>
                        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                            <rect x="5.66406" y="19.8064" width="2" height="20" rx="1" transform="rotate(-135 5.66406 19.8064)" fill="white" />
                            <rect x="4.25" y="5.66577" width="2" height="20" rx="1" transform="rotate(-45 4.25 5.66577)" fill="white" />
                        </svg>
                    </div>
                </div>
            )}

            <Modal
                className="consent-confirmation-modal"
                centered
                destroyOnClose
                width={516}
                open={isConsentOpen}
                title="Consent confirmation"
                onCancel={() => { setIsConsentOpen(false); setConsentType(undefined); setConsentPartyId("") }}
                footer={[
                    <Button key="cancel" type="link" onClick={() => setIsConsentOpen(false)}>Cancel</Button>,
                    <Button key="ok" type="primary" disabled={!consentType} onClick={confirmConsent}>Confirm</Button>,
                ]}
            >
                <div className="consent-confirmation-content">
                    <div className="consent-confirmation-content-tips">
                        Please maintain the necessary documentation evidencing the borrower&apos;s consent if you obtain credit consent outside of Zeitro&apos;s borrower application.
                    </div>
                    <div className="consent-confirmation-content-select">
                        <div className="consent-select-label">How was consent received?</div>
                        <Select
                            value={consentType}
                            style={{ width: "100%" }}
                            onChange={setConsentType}
                            options={[{ value: "email", label: "Email" }]}
                            placeholder="Please select"
                        />
                    </div>
                </div>
            </Modal>
        </div>
    )
}
